import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * NEGOCIO ACTIVO del panel.
 *
 * D2 · UN SOLO NEGOCIO A LA VEZ. No hay vista unificada: lo que se ve en toda
 * la aplicación es lo de ESTE workspace. Cambiar el selector cambia todo.
 *
 * El id elegido viaja al servidor en la cabecera `X-Workspace-Id` (la inyecta
 * `api/client.ts` en cada petición). El servidor lo valida contra la
 * organización del usuario: mandar uno ajeno es 403, no datos de otro.
 *
 * Se persiste en localStorage para que recargar la página no devuelva al
 * usuario al primer negocio de la lista. Al cerrar sesión se limpia.
 */

export interface WorkspaceResumen {
  id: string;
  name: string;
  slug: string;
  currencyPrefix: string;
  timezone: string;
  /** nuevo = vive en esta Conversia; reflejo/vip/pendiente = se ve en «Mi Conversia». */
  tipoCliente?: 'nuevo' | 'reflejo' | 'vip' | 'pendiente';
  /** El negocio desde donde el OWNER maneja a todos los clientes (módulo `operacion`). */
  conOperacion?: boolean;
  /** Su color de marca (`#rrggbb`): dentro de este negocio, la piel lo usa de acento. null = magenta. */
  colorMarca?: string | null;
  /** URL de su logo: va en lugar del monograma. null = el monograma de 2 letras. */
  logoMarca?: string | null;
}

interface WorkspaceState {
  /** Negocios que el usuario puede ver (todos los de su organización). */
  items: WorkspaceResumen[];
  /** Id del negocio activo. `null` mientras no se ha cargado la lista. */
  activeId: string | null;
  /**
   * ¿Se pinta el selector? Lo decide el SERVIDOR (conoce
   * `MULTI_WORKSPACE_ENABLED`), no el panel. En una instancia de cliente llega
   * `false` y la interfaz queda igual que antes de que existieran los
   * workspaces — D5.
   */
  mostrarSelector: boolean;
  /** ¿La instancia admite varios negocios? Informativo. */
  multiWorkspace: boolean;
  /** Ya se resolvió la lista al menos una vez (para no parpadear al arrancar). */
  cargado: boolean;

  setWorkspaces: (input: {
    items: WorkspaceResumen[];
    mostrarSelector: boolean;
    multiWorkspace: boolean;
  }) => void;
  setActive: (id: string) => void;
  clear: () => void;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      items: [],
      activeId: null,
      mostrarSelector: false,
      multiWorkspace: false,
      cargado: false,

      setWorkspaces: ({ items, mostrarSelector, multiWorkspace }) => {
        const actual = get().activeId;
        // Se conserva el activo si sigue existiendo; si no (le quitaron el
        // acceso, o es el primer arranque), cae al primero de la lista.
        const sigueValiendo = actual && items.some((w) => w.id === actual);
        set({
          items,
          mostrarSelector,
          multiWorkspace,
          cargado: true,
          activeId: sigueValiendo ? actual : (items[0]?.id ?? null),
        });
      },

      setActive: (id) => {
        if (!get().items.some((w) => w.id === id)) return; // no se acepta uno ajeno
        set({ activeId: id });
      },

      clear: () =>
        set({
          items: [],
          activeId: null,
          mostrarSelector: false,
          multiWorkspace: false,
          cargado: false,
        }),
    }),
    { name: 'conversia.workspace' },
  ),
);

/**
 * Id del negocio activo, para quien no puede usar hooks (el cliente HTTP).
 * Devuelve `null` si todavía no se cargó la lista: en ese caso no se manda la
 * cabecera y el servidor resuelve solo si el usuario tiene uno único.
 */
export function getActiveWorkspaceId(): string | null {
  return useWorkspaceStore.getState().activeId;
}

/** Selector: el logo del negocio activo (punto 11), o `null`. Lo usan la barra lateral y el menú del teléfono. */
export const logoDelActivo = (s: { items: WorkspaceResumen[]; activeId: string | null }): string | null =>
  s.items.find((w) => w.id === s.activeId)?.logoMarca ?? null;

/** El negocio activo completo, o `null`. */
export function getActiveWorkspace(): WorkspaceResumen | null {
  const { items, activeId } = useWorkspaceStore.getState();
  return items.find((w) => w.id === activeId) ?? null;
}
